import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  CookieConsentChoice,
  getStoredConsentChoice,
  setConsentChoice as persistConsentChoice,
} from '../utils/analytics';

type CookieConsentContextValue = {
  choice: CookieConsentChoice | null;
  bannerOpen: boolean;
  openCookieSettings: () => void;
  acceptAll: () => void;
  acceptNecessaryOnly: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [choice, setChoice] = useState<CookieConsentChoice | null>(() => getStoredConsentChoice());
  const [bannerOpen, setBannerOpen] = useState(() => getStoredConsentChoice() === null);

  const acceptAll = useCallback(() => {
    persistConsentChoice('all');
    setChoice('all');
    setBannerOpen(false);
  }, []);

  const acceptNecessaryOnly = useCallback(() => {
    persistConsentChoice('necessary');
    setChoice('necessary');
    setBannerOpen(false);
  }, []);

  const openCookieSettings = useCallback(() => {
    setBannerOpen(true);
  }, []);

  const value = useMemo(
    () => ({
      choice,
      bannerOpen,
      openCookieSettings,
      acceptAll,
      acceptNecessaryOnly,
    }),
    [choice, bannerOpen, openCookieSettings, acceptAll, acceptNecessaryOnly]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export const useCookieConsent = (): CookieConsentContextValue => {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return ctx;
};
